"use client";

import * as React from "react";
import { Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ContactFormProps {
  accent?: "red" | "purple" | "amber-gold" | "cyan";
  title?: string;
  subtitle?: string;
  includeOrg?: boolean;
}

export function ContactForm({
  accent = "amber-gold",
  title = "Send a message.",
  subtitle = "Every inquiry is reviewed personally.",
  includeOrg = true,
}: ContactFormProps) {
  const [state, setState] = React.useState<"idle" | "sent">("idle");
  const [data, setData] = React.useState({ name: "", email: "", org: "", message: "" });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (data.email.includes("@") && data.message.length > 5) {
      setState("sent");
      setData({ name: "", email: "", org: "", message: "" });
      setTimeout(() => setState("idle"), 6000);
    }
  };

  const ringClass = {
    red: "focus:border-red focus:ring-red/30",
    purple: "focus:border-purple focus:ring-purple/30",
    "amber-gold": "focus:border-amber-gold focus:ring-amber-gold/30",
    cyan: "focus:border-cyan focus:ring-cyan/30",
  }[accent];

  const btnClass = {
    red: "bg-gradient-sovereign",
    purple: "bg-gradient-royal",
    "amber-gold": "bg-gradient-premium",
    cyan: "bg-gradient-clarity",
  }[accent];

  return (
    <>
      <div className="text-overline text-amber-gold mb-2">Direct inquiry</div>
      <h3 className="text-heading-md mb-1">{title}</h3>
      <p className="text-caption text-muted-foreground mb-5">{subtitle}</p>
      <form onSubmit={onSubmit} className="space-y-3">
        <input
          type="text"
          placeholder="Full name"
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          className={`w-full px-3 h-11 rounded-lg glass border border-border/60 text-body-sm ${ringClass} focus:outline-none focus:ring-2 transition-all`}
        />
        <input
          type="email"
          required
          placeholder="Email address"
          value={data.email}
          onChange={(e) => setData({ ...data, email: e.target.value })}
          className={`w-full px-3 h-11 rounded-lg glass border border-border/60 text-body-sm ${ringClass} focus:outline-none focus:ring-2 transition-all`}
        />
        {includeOrg && (
          <input
            type="text"
            placeholder="Organization (optional)"
            value={data.org}
            onChange={(e) => setData({ ...data, org: e.target.value })}
            className={`w-full px-3 h-11 rounded-lg glass border border-border/60 text-body-sm ${ringClass} focus:outline-none focus:ring-2 transition-all`}
          />
        )}
        <textarea
          required
          placeholder="Your message — what needs to be won?"
          value={data.message}
          onChange={(e) => setData({ ...data, message: e.target.value })}
          rows={5}
          className={`w-full px-3 py-2 rounded-lg glass border border-border/60 text-body-sm ${ringClass} focus:outline-none focus:ring-2 transition-all resize-none`}
        />
        <Button
          type="submit"
          size="lg"
          className={`w-full h-12 ${btnClass} shadow-lg`}
          disabled={state === "sent"}
        >
          {state === "sent" ? (
            <>
              <Sparkles className="size-4" /> Sent — Confirmation Inbound
            </>
          ) : (
            <>
              <Send className="size-4" /> Send Inquiry
            </>
          )}
        </Button>
      </form>
      <p className="mt-4 text-caption text-muted-foreground text-center">
        Discretion is absolute. Confidentiality is guaranteed.
      </p>
    </>
  );
}

export function NewsletterForm({ accent = "amber-gold" }: { accent?: "red" | "purple" | "amber-gold" | "cyan" }) {
  const [email, setEmail] = React.useState("");
  const [subscribed, setSubscribed] = React.useState(false);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.includes("@")) {
      setSubscribed(true);
      setEmail("");
      setTimeout(() => setSubscribed(false), 5000);
    }
  };

  const btnClass = {
    red: "bg-gradient-sovereign",
    purple: "bg-gradient-royal",
    "amber-gold": "bg-gradient-premium",
    cyan: "bg-gradient-clarity",
  }[accent];

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="relative">
        <input
          type="email"
          required
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`w-full pl-4 pr-3 h-12 rounded-lg glass border border-border/60 text-body-md ${
            accent === "amber-gold" ? "focus:border-amber-gold focus:ring-amber-gold/30" :
            accent === "red" ? "focus:border-red focus:ring-red/30" :
            accent === "purple" ? "focus:border-purple focus:ring-purple/30" :
            "focus:border-cyan focus:ring-cyan/30"
          } focus:outline-none focus:ring-2 transition-all`}
        />
      </div>
      <Button type="submit" size="lg" className={`w-full h-12 ${btnClass} shadow-lg`} disabled={subscribed}>
        {subscribed ? (
          <>
            <Sparkles className="size-4" /> Subscribed
          </>
        ) : (
          <>
            <Send className="size-4" /> Subscribe
          </>
        )}
      </Button>
    </form>
  );
}
